import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { BoardServices } from '../../lib/api/board.services'
import { handleApiError } from '../../lib/utils/handleApiError'
import {
	DefaultCardParamsProps,
	DefaultStatusParamsProps,
	GetBoardDataResponse,
	GetBoardPermissionsDataResponse,
	GetBoardsDataResponse,
	GetBoardStatusesDataResponse,
	GetStatusCardDataResponse,
	UpdateBoardUsersParamsProps,
	UpdateCardParamsProps,
	UpdateStatusParamsProps,
	MoveStatusDataProps,
	MoveCardDataProps,
	GetCardCommentDataResponse,
	DefaultCardCommentDataProps,
	UpdateCardCommentParamsProps,
	DefaultCardCommentParamsProps,
} from '../../lib/api/types/board.interfaces'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import toastCaller from '../../lib/utils/toastCaller'

// Helper function to update board data
const updateBoardData = (
	queryClient: any,
	boardId: string,
	updater: (oldData: GetBoardDataResponse) => GetBoardDataResponse,
) => {
	queryClient.setQueryData(['board', boardId], updater)
}

// Helper function to convert Card to BoardStatusCard
// const convertToBoardStatusCard = (card: BoardStatusCard): BoardStatusCard => ({
// 	id: card.id,
// 	title: card.title,
// 	description: card.content ?? '',
// 	priority: card.priority || 1, // medium priority by default
// 	createdBy: card.createdBy,
// 	assignedTo: card.assignedTo || null,
// 	createdAt: card.createdAt,
// 	updatedAt: card.modifiedAt,
// 	completedAt: card.completedAt,
// 	dueDate: card.dueDate,
// 	statusId: card.status?.id || card.statusId,
// })

//boards
export const useBoards = (
	options?: Partial<QueryObserverOptions<GetBoardsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetBoardsDataResponse>({
		queryKey: ['boards'],
		queryFn: () => BoardServices.getBoards(),
		enabled: isAuthenticated,
		...options,
	})
}

export const useCreateBoard = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['boardCreate'],
		mutationFn: ({
			name,
			files,
		}: {
			name: string
			files?: string[]
			invalidate?: boolean
		}) => BoardServices.createBoard({ name, files}),
		onError: (error, { name }) => handleApiError(error, name),
		onSuccess: (response, { invalidate }) => {
			const board = response.data.data
			queryClient.setQueryData(['boards'], (oldData: any) => {
				if (!oldData?.data) return oldData
				return {
					...oldData,
					data: [
						{
							id: board.id,
							name: board.name,
							users: board.users ?? 0,
							cards: board.cards ?? 0,
						},
						...oldData.data,
					],
				}
			})
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['boards'],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useBoard = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetBoardDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetBoardDataResponse>({
		queryKey: ['board', id],
		queryFn: () => BoardServices.getBoard({ id}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

export const useUpdateBoard = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updateBoard'],
		mutationFn: ({
			data,
			id,
		}: {
			data: { name: string; files?: string[] }
			id: string
			invalidate?: boolean
		}) => BoardServices.updateBoard({ data, id}),
		onError: (error, { data }) => handleApiError(error, data?.name),
		onSuccess: (response, { id, data, invalidate }) => {
			queryClient.setQueryData(['boards'], (oldData: any) => {
				if (!oldData?.data) return oldData
				return {
					...oldData,
					data: oldData.data.map((board: any) =>
						board.id === id ? { ...board, name: data.name } : board,
					),
				}
			})

			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['boards'],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteBoard = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['boardDelete'],
		mutationFn: ({ id }: DefaultGetParamsProps) =>
			BoardServices.deleteBoard({ id}),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { id}) => {
			queryClient.setQueryData(['boards'], (oldData: any) => {
				if (!oldData?.data) return oldData
				return {
					...oldData,
					data: oldData.data.filter((board: any) => board.id !== id),
				}
			})
			queryClient.invalidateQueries({
				queryKey: ['group', id],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useBoardPermissions = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetBoardPermissionsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetBoardPermissionsDataResponse>({
		queryKey: ['group', id],
		queryFn: () => BoardServices.getBoardPermissions({ id}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

export const useUpdateBoardPermissions = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updateBoardPermissions'],
		mutationFn: ({ data, id}: UpdateBoardUsersParamsProps) =>
			BoardServices.updateBoardPermissions({ data, id}),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { id, invalidate }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['boards'],
				})
			}
			queryClient.invalidateQueries({
				queryKey: ['board', id],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

// statuses (columns)
export const useStatuses = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetBoardStatusesDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetBoardStatusesDataResponse>({
		queryKey: ['statuses'],
		queryFn: () => BoardServices.getStatusesByBoardId({ id}),
		enabled: isAuthenticated,
		...options,
	})
}

export const useCreateStatus = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['statusCreate'],
		mutationFn: (data: Omit<UpdateStatusParamsProps, 'statusId'>) =>
			BoardServices.createStatus(data),
		onError: (error, { data }) => handleApiError(error, data?.name),
		onSuccess: (_, { boardId }) => {
			toastCaller({
				type: 'success',
				message: 'Column successfully created!',
			})

			queryClient.invalidateQueries({
				queryKey: ['board', boardId],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateStatus = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updateStatus'],
		mutationFn: (data: UpdateStatusParamsProps) =>
			BoardServices.updateStatus(data),
		onError: (error, { data }) => handleApiError(error, data?.name),
		onSuccess: (_, { boardId }) => {
			toastCaller({ type: 'success', message: 'Column successfully updated!' })
			queryClient.invalidateQueries({
				queryKey: ['board', boardId],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteStatus = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['statusDelete'],
		mutationFn: (data: DefaultStatusParamsProps) =>
			BoardServices.deleteStatus(data),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { boardId }) => {
			queryClient.invalidateQueries({
				queryKey: ['board', boardId],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useMoveStatus = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['statusMove'],
		mutationFn: async ({
			boardId,
			statusId,
			data,
		}: {
			boardId: string
			statusId: string
			data: MoveStatusDataProps
		}) => {
			const response = await BoardServices.moveStatus({
				boardId,
				statusId,
				data,
			})
			return response.data
		},
		onError: (error) => handleApiError(error),
		onSuccess: (_, { boardId, data, statusId }) => {
			updateBoardData(queryClient, boardId, (oldData) => {
				if (!oldData) return oldData

				const statuses = [...oldData.data.statuses]
				const statusIndex = statuses.findIndex((s) => s.id === statusId)
				if (statusIndex === -1) return oldData

				// Remove status from old position
				const [movedStatus] = statuses.splice(statusIndex, 1)
				// Insert at new position
				statuses.splice(data.position, 0, movedStatus)

				return {
					...oldData,
					data: {
						...oldData.data,
						statuses,
					},
				}
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

// cards
export const useCard = (
	{ boardId, cardId}: DefaultCardParamsProps,
	options?: Partial<QueryObserverOptions<GetStatusCardDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetStatusCardDataResponse>({
		queryKey: ['card', boardId, cardId],
		queryFn: () => BoardServices.getCard({ boardId, cardId}),
		enabled: isAuthenticated && Boolean(cardId && boardId),
		retry: 0,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		...options,
	})
}

export const useCreateCard = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['cardCreate'],
		mutationFn: async ({ data,
			boardId,
		}: Omit<UpdateCardParamsProps, 'cardId'>) => {
			const response = await BoardServices.createCard({ data, boardId })
			return { ...response.data, statusId: data.statusId }
		},
		onError: (error, { data }) => handleApiError(error, data?.title),
		onSuccess: (
			response: GetStatusCardDataResponse & { statusId: string },
			{ boardId},
		) => {
			toastCaller({ type: 'success', message: 'Task successfully created!' })
			updateBoardData(queryClient, boardId, (oldData) => {
				if (!oldData || !response?.data) return oldData

				return {
					...oldData,
					data: {
						...oldData.data,
						statuses: oldData.data.statuses.map((status) => {
							if (status.id === response.statusId) {
								return {
									...status,
									cards: [...status.cards, response?.data],
								}
							}
							return status
						}),
					},
				}
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateCard = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['cardUpdate'],
		mutationFn: async ({ data,
			boardId,
			cardId,
		}: UpdateCardParamsProps) => {
			const response = await BoardServices.updateCard({ data,
				boardId,
				cardId,
			})
			return response.data
		},
		onError: (error, { data }) => handleApiError(error, data?.title),
		onSuccess: (response: GetStatusCardDataResponse, { boardId}) => {
			toastCaller({ type: 'success', message: 'Task successfully updated!' })
			updateBoardData(queryClient, boardId, (oldData) => {
				if (!oldData) return oldData
				return {
					...oldData,
					data: {
						...oldData.data,
						statuses: oldData.data.statuses.map((status) => ({
							...status,
							cards: status.cards.map((card) =>
								card.id === response?.data?.id ? response?.data : card,
							),
						})),
					},
				}
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteCard = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['cardDelete'],
		mutationFn: ({ boardId, cardId}: DefaultCardParamsProps) =>
			BoardServices.deleteCard({ boardId, cardId}),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { boardId, cardId}) => {
			toastCaller({ type: 'success', message: 'Task successfully deleted!' })
			updateBoardData(queryClient, boardId, (oldData) => {
				if (!oldData) return oldData

				return {
					...oldData,
					data: {
						...oldData.data,
						statuses: oldData.data.statuses.map((status) => ({
							...status,
							cards: status.cards.filter((card) => card.id !== cardId),
						})),
					},
				}
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useMoveCard = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['cardMove'],
		mutationFn: async ({
			boardId,
			cardId,
			data,
		}: {
			boardId: string
			cardId: string
			data: MoveCardDataProps
		}) => {
			const response = await BoardServices.moveCard({
				boardId,
				cardId,
				data,
			})
			return response.data
		},
		onError: (error) => handleApiError(error),
		onSuccess: (_, { boardId, data, cardId }) => {
			updateBoardData(queryClient, boardId, (oldData) => {
				if (!oldData) return oldData

				const statuses = [...oldData.data.statuses]
				// Find source and target statuses
				const sourceStatus = statuses.find((s) =>
					s.cards.some((c) => c.id === cardId),
				)
				const targetStatus = statuses.find((s) => s.id === data.statusId)

				if (!sourceStatus || !targetStatus) return oldData

				// Find and remove card from source status
				const cardIndex = sourceStatus.cards.findIndex((c) => c.id === cardId)
				if (cardIndex === -1) return oldData
				let [movedCard] = sourceStatus.cards.splice(cardIndex, 1)

				// if complete: true,update createdAt
				if (targetStatus.complete === true) {
					movedCard = { ...movedCard, completedAt: new Date().toISOString() }
				}

				// Insert card into target status at new position
				targetStatus.cards.splice(data.position, 0, {
					...movedCard,
					statusId: data.statusId,
				})

				return {
					...oldData,
					data: {
						...oldData.data,
						statuses,
					},
				}
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useCardComments = (
	{ boardId, cardId}: DefaultCardParamsProps,
	options?: Partial<QueryObserverOptions<GetCardCommentDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetCardCommentDataResponse>({
		queryKey: ['cardComments', boardId, cardId],
		queryFn: () => BoardServices.getCardComments({ boardId, cardId}),
		enabled: isAuthenticated && Boolean(cardId && boardId),
		retry: 0,
		...options,
	})
}

export const useCreateCardComment = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['cardCommentCreate'],
		mutationFn: async ({
			boardId,
			cardId,
			data,
		}: {
			boardId: string
			cardId: string
			data: DefaultCardCommentDataProps
		}) => {
			const response = await BoardServices.createCardComment({
				boardId,
				cardId,
				data,
			})
			return response.data
		},
		onError: (error) => handleApiError(error),
		onSuccess: (response, { boardId, cardId}) => {
			if (!response?.data) return
			queryClient.setQueryData(
				['cardComments', boardId, cardId],
				(oldData: any) => {
					if (!oldData) {
						return { data: [response?.data] }
					}

					return {
						...oldData,
						data: [...(oldData.data || []), response?.data],
					}
				},
			)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateCardComment = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['cardCommentUpdate'],
		mutationFn: async ({ data,
			boardId,
			cardId,
			commentId,
		}: UpdateCardCommentParamsProps) => {
			const response = await BoardServices.updateCardComment({ data,
				boardId,
				cardId,
				commentId,
			})
			return response.data
		},
		onError: (error, { data }) => handleApiError(error),
		onSuccess: (_, { boardId, cardId, commentId, data }) => {
			queryClient.setQueryData(
				['cardComments', boardId, cardId],
				(oldData: any) => {
					if (!oldData) return oldData

					return {
						...oldData,
						data: oldData.data.map((comment: any) =>
							comment.id === commentId
								? {
										...comment,
										content: data.content,
										modifiedAt: new Date().toISOString(),
									}
								: comment,
						),
					}
				},
			)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteCardComment = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['cardCommentDelete'],
		mutationFn: ({
			boardId,
			cardId,
			commentId,
		}: DefaultCardCommentParamsProps) =>
			BoardServices.deleteCardComment({ boardId, cardId, commentId}),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { boardId, cardId, commentId }) => {
			queryClient.setQueryData(
				['cardComments', boardId, cardId],
				(oldData: any) => {
					if (!oldData) return oldData

					return {
						...oldData,
						data: oldData.data.filter(
							(comment: any) => comment.id !== commentId,
						),
					}
				},
			)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
